<template v-if="application">
  <tile>
    <div class="application-status-wrapper">
      <div class="header">
        <div class="cancel-header">
          <p class="title">{{ productName }}</p>
          <a
            v-if="showCancellationLink && productTypeId === ProductIds.PEL"
            class="cancellation-link"
            @click="openCancellationModal"
            >Cancel Application</a
          >
        </div>

        <p>Application ID: {{ applicationDetail.referenceId }}</p>
        <p>
          {{ application.created ? `Started on ${applicationDetail.createdAt}` : "Started on" }}
          |
          {{
            applicationDetail.expiredAt ? `Expires on ${applicationDetail.expiredAt}` : "Expires on"
          }}
        </p>
      </div>
      <div
        class="redirect-button-container"
        v-if="showSLCButtonLOC && productTypeId === ProductIds.LOC"
      >
        <SLCRedirectButton :openInNewTab="false" />
      </div>
      <div class="application-status">
        <div class="header">
          <p>Application Status</p>
          <a
            v-if="
              showCancellationLink &&
              (productTypeId === ProductIds.REFI || productTypeId === ProductIds.LOC)
            "
            class="cancellation-link"
            @click="openCancellationModal"
            >Cancel Application</a
          >
          <cancellation-modal v-if="cancelApplication" :application="application" />
        </div>
        <loader v-if="gettingData" />
        <component
          v-else
          :is="componentName"
          v-bind="currentProperties"
          v-on="currentListeners"
        ></component>
      </div>
    </div>
  </tile>
  <CertificationUpdatesTile
    v-if="showCertificationUpdatesTile"
    ref="certUpdatesTileRef"
    id="certification-updates"
    :certifications="certifications"
    :loading="gettingData"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import Tile from "@/components/shared/Tile.vue";
import CancellationModal from "@/components/modals/CancellationModal.vue";
import useFileUtilities from "@/use/fileUtilities";
import Complete from "@/components/application-statuses/Complete.vue";
import Cancelled from "@/components/application-statuses/Cancelled.vue";
import Declined from "@/components/application-statuses/Declined.vue";
import EvaluationQueue from "@/components/application-statuses/EvaluationQueue.vue";
import NotSubmitted from "@/components/application-statuses/NotSubmitted.vue";
import SubmittedAndProcessing from "@/components/application-statuses/SubmittedAndProcessing.vue";
import RightToCancelPeriod from "@/components/application-statuses/RightToCancelPeriod.vue";
import SchoolCertification from "@/components/application-statuses/SchoolCertification.vue";
import CertificationUpdatesTile from "@/components/certification-updates/CertificationUpdatesTile.vue";
import { ApplicationStatusTypeIds, ProductIds } from "@/config/constants";
import type { ApplicationDetail } from "@/models/application";
import useApplicationStatus from "@/composables/application-status/useApplicationStatus";
import Loader from "@/components/shared/Loader.vue";
import { DocumentTypes } from "@/config/enums";
import useApplicationData from "@/composables/useApplicationData";
import SLCRedirectButton from "@/components/buttons/SLCRedirectButton.vue";
import useLoanData from "@/composables/loan/useLoanData";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";

const certUpdatesTileRef = ref<InstanceType<typeof CertificationUpdatesTile> | null>(null);

const store = useStore();

const { productTypeId, timeline, currentstep, setCurrentStep } = useApplicationStatus();
const { application } = useApplicationData();
const { base64ToBlob } = useFileUtilities();
const { loan, fundsDisbursed, loanBoarded } = useLoanData();
const { details } = useApplicationInfo();

const applicationDetail = computed<ApplicationDetail>(() => store.state?.applicationDetail);

const showCertificationUpdatesTile = computed<boolean>(() => {
  return details.value?.showCertificationUpdatesTile === true;
});

const certifications = computed(() => {
  return store.state.loanConfigurationModule?.certifications;
});
const emit = defineEmits<{
  (e: "todoListClicked"): void;
  (e: "viewCertificationUpdates"): void;
}>();

const props = defineProps({});

// template refs
const fileAccess = ref() as any;

// Data
const docAvailable = ref<boolean | undefined>(undefined);
const gettingData = ref<boolean>(true);

const componentName = computed(() => {
  return getStatusComponent();
});
const currentProperties = computed(() => {
  switch (componentName.value) {
    case Declined:
      return { noticeReady: noticeReady.value };
    default:
      return undefined;
  }
});
const currentListeners = computed(() => {
  if (
    componentName.value === EvaluationQueue ||
    componentName.value === NotSubmitted ||
    componentName.value === RightToCancelPeriod ||
    componentName.value === SubmittedAndProcessing
  ) {
    return {
      todoListClicked: handleToDoListClickedEvent,
    };
  } else if (componentName.value === Declined) {
    return {
      downloadAdverseActionNotice: handleDownloadAdverseActionNoticeEvent,
    };
  } else if (componentName.value === SchoolCertification || componentName.value === Complete) {
    return {
      viewCertificationUpdates: () => {
        emit("viewCertificationUpdates");
      },
    };
  } else {
    return {};
  }
});
const noticeReady = computed(() => (docAvailable.value ? docAvailable.value : false));
const productName = computed(() => store.getters["productDisplayName"]);
const applicationId = computed(() => application.value?.id);
const cancelApplication = computed(() => store.getters["modal/cancellationModalActive"]);
const applicationStatusId = computed(() => store.state.appData?.application?.applicationStatusId);
const showCancellationLink = computed(() => {
  return (
    applicationStatusId.value === ApplicationStatusTypeIds.NOT_SUBMITTED ||
    applicationStatusId.value === ApplicationStatusTypeIds.EVALUATION ||
    applicationStatusId.value === ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING ||
    applicationStatusId.value === ApplicationStatusTypeIds.RIGHT_TO_CANCEL ||
    applicationStatusId.value === ApplicationStatusTypeIds.SCHOOL_CERTIFICATION
  );
});

const showSLCButtonLOC = computed<boolean>(() => {
  return (
    applicationStatusId.value === ApplicationStatusTypeIds.COMPLETE &&
    productTypeId.value === ProductIds.LOC &&
    fundsDisbursed.value &&
    loanBoarded.value
  );
});
function handleToDoListClickedEvent() {
  if (
    componentName.value === EvaluationQueue ||
    componentName.value === NotSubmitted ||
    componentName.value === RightToCancelPeriod ||
    componentName.value === SubmittedAndProcessing
  ) {
    emit("todoListClicked");
  } else {
    return;
  }
}
function handleDownloadAdverseActionNoticeEvent() {
  if (componentName.value === Declined) {
    downloadAdverseActionNotice();
  } else {
    return;
  }
}

function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function openCancellationModal() {
  store.dispatch("modal/openCancellationModal");
}

function getStatusComponent() {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.CANCELLED:
      return Cancelled;
    case ApplicationStatusTypeIds.NOT_SUBMITTED:
      return NotSubmitted;
    case ApplicationStatusTypeIds.COMPLETE:
      return Complete;
    case ApplicationStatusTypeIds.DECLINED:
    case ApplicationStatusTypeIds.SMR_DECLINED:
      return Declined;
    case ApplicationStatusTypeIds.EVALUATION:
      return EvaluationQueue;
    case ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING:
      return SubmittedAndProcessing;
    case ApplicationStatusTypeIds.RIGHT_TO_CANCEL:
      return RightToCancelPeriod;
    case ApplicationStatusTypeIds.SCHOOL_CERTIFICATION:
      return SchoolCertification;
    default:
      return undefined;
  }
}

async function checkForLetter() {
  const notice = await $api.documents.getApplicationDocumentByType(
    applicationId.value,
    Number(DocumentTypes.AdverseAction),
  );

  if (notice && notice.length > 0) {
    const b64 = await $api.documents.getDocument(notice.pop().documentId);

    if (b64) {
      docAvailable.value = true;
    } else {
      docAvailable.value = false;
    }
  }
}
async function downloadAdverseActionNotice() {
  const notice = await $api.documents.getApplicationDocumentByType(applicationId.value, 25);
  const b64 = await $api.documents.getDocument(notice.pop().documentId);

  console.log("b64", b64);
  const blob = base64ToBlob(b64);
  console.log(`blob ${new Date()}`, blob);

  if (blob.size > 0) {
    const blobURL = URL.createObjectURL(blob);
    let a = fileAccess as HTMLAnchorElement;
    // console.log("this.$refs.link", this.$refs.link);
    a.setAttribute("download", "Application.pdf");
    a.setAttribute("href", `${blobURL}`);
    a.click();
    URL.revokeObjectURL(blobURL);
  } else {
    // #TODO do they want messaging if download fails?
    console.log("FAILED TO DOWNLOAD");
  }
}

const applicationStatusModule = computed(() => store.state.applicationStatusModule);

onMounted(async () => {
  gettingData.value = true;

  await store.dispatch("getApplicationDetails", applicationId.value);

  await store.dispatch("applicationStatusModule/getTimelineAsync", {
    applicationId: applicationId.value,
    applicantId: store.state.appData?.currentApplicant?.id,
  });

  setCurrentStep(applicationStatusModule.value.selectedStep);

  if (
    applicationStatusId.value === ApplicationStatusTypeIds.DECLINED ||
    applicationStatusId.value === ApplicationStatusTypeIds.SMR_DECLINED
  ) {
    await checkForLetter();
  }

  gettingData.value = false;
});

watch(
  () => applicationStatusId.value,
  async (value: any) => {
    if (value) {
      if (
        applicationStatusId.value === ApplicationStatusTypeIds.DECLINED ||
        applicationStatusId.value === ApplicationStatusTypeIds.SMR_DECLINED
      ) {
        await checkForLetter();
      }
    }
  },
);

defineExpose({
  timeline,
  $refs: {
    certUpdatesTileRef,
  },
  currentstep,
});
</script>

<style lang="scss">
.application-status-wrapper {
  .header {
    margin-bottom: 1rem;

    p {
      margin-bottom: 0;
    }

    .title {
      font-weight: 700;
      font-size: 1.25rem;
    }
  }

  .application-status {
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      a {
        color: blue;
        text-decoration: underline;
        cursor: pointer;
      }
    }

    .row {
      display: flex;
    }

    .col-10 {
      flex: 10%;
    }

    .col-90 {
      flex: 90%;
    }

    .col-50 {
      flex: 50%;
    }

    .inline-link {
      text-decoration: underline;
      cursor: pointer;
    }

    .status-bar {
      .step + .step {
        margin-top: 1.5em;
      }

      .step > div:first-child {
        position: static;
        height: 0;
      }

      .step > div:not(:first-child) {
        margin-left: 1.5em;
        padding-left: 1em;
      }

      .step:last-child .circle:after {
        display: none;
      }
    }
  }
}
.divider {
  border-left: 1px solid #ced1d9;
  height: auto;
  transform: scaleY(0.75);
}

.status-bar {
  min-width: 50%;
}

.status-wrapper {
  display: flex;
  flex-direction: row;
  // justify-content: space-between;
}

.status-wrapper .opal-alert {
  color: #bb0000;
  font-weight: var(--font-weight-medium);
}

.cancel-header {
  display: flex;
  align-items: center;
  gap: 60px;
  width: 100%;
  a {
    color: blue;
    text-decoration: underline;
    cursor: pointer;
  }
}

.redirect-button-container {
  margin-bottom: 1rem;
}

@media (max-width: 992px) {
  .redirect-button-container {
    position: absolute;
    top: 0;
    right: 0;
    margin: 1rem;
  }
}

@media (max-width: 800px) {
  .redirect-button-container {
    margin-bottom: 1rem;
    position: relative;
  }
}

@media (max-width: 1210px) {
  .status-bar {
    min-width: 100%;
  }

  .divider {
    display: none;
  }
}
</style>
